import { Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * EmptyState — the single unified empty state across the panel.
 *
 * Design language:
 *   - Container: rounded-2xl border, subtle surface bg (not solid gray) — matches cards system.
 *   - Icon chip: 14×14 rounded-2xl with hairline border and muted-40 fill (matches PageHeader chip style, upscaled).
 *   - Title: font-display 2xl bold — same weight/family as page H1.
 *   - Description: text-sm muted, leading-relaxed, max-w-md centered.
 *   - CTA: bg-brand primary button (h-10), title-case label.
 *
 * Contract:
 *   - `icon`        lucide Icon (defaults to Inbox — "no data" semantic)
 *   - `title`       short heading ("Henüz beden serisi yok")
 *   - `description` supporting paragraph
 *   - `action`      { label, onClick, icon?, testId? } — primary CTA, optional
 *   - `secondary`   optional secondary render prop (extra content below CTA — templates, tips)
 *   - `testId`      optional data-testid for the wrapper
 */
export function EmptyState({
    icon: Icon = Inbox,
    title,
    description,
    action,
    secondary,
    testId,
}) {
    const ActionIcon = action?.icon;
    return (
        <div className="rounded-2xl border border-border bg-surface overflow-hidden" data-testid={testId}>
            <div className="px-8 py-14 text-center max-w-xl mx-auto">
                <div className="inline-flex h-14 w-14 rounded-2xl border border-border bg-muted/40 items-center justify-center mb-5">
                    <Icon className="h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
                </div>
                {title && (
                    <h2 className="font-display text-2xl font-bold tracking-tight">{title}</h2>
                )}
                {description && (
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{description}</p>
                )}
                {action && (
                    <Button
                        size="lg"
                        onClick={action.onClick}
                        className="mt-6 h-10 bg-brand hover:bg-brand/90 text-brand-fg text-xs gap-1.5"
                        data-testid={action.testId}
                    >
                        {ActionIcon && <ActionIcon className="h-3.5 w-3.5" />}
                        {action.label}
                    </Button>
                )}
            </div>
            {secondary && (
                <div className="border-t border-border bg-muted/20 px-6 py-5">{secondary}</div>
            )}
        </div>
    );
}
